import { WORKOUT_TIMER_KEY, clearJson, readJson, writeJson } from './persistentTime';

/**
 * How long the current workout has been running, shown in the site header.
 *
 * Starts when a workout starts and freezes when it finishes, staying on screen
 * until the next workout begins. Held client-side in localStorage so it
 * survives refreshes and browser restarts; `startedAt` is seeded from the
 * session's own start time where possible, so it stays truthful even if the
 * record is created late (a refresh mid-workout, say).
 */
export interface WorkoutTimerRecord {
  sessionId: string;
  /** Epoch milliseconds. */
  startedAt: number;
  /** Epoch milliseconds, or null while the workout is still running. */
  endedAt: number | null;
}

export function readWorkoutTimer(): WorkoutTimerRecord | null {
  const rec = readJson<WorkoutTimerRecord>(WORKOUT_TIMER_KEY);
  if (!rec || typeof rec.sessionId !== 'string' || !Number.isFinite(rec.startedAt)) return null;
  return rec;
}

/**
 * Begins (or adopts) the timer for a session. Re-running for the same session
 * keeps the original start time, so a refresh cannot restart the clock.
 */
export function startWorkoutTimer(sessionId: string, startedAtMs: number): void {
  const existing = readWorkoutTimer();
  if (existing && existing.sessionId === sessionId && existing.endedAt === null) return;
  writeJson(WORKOUT_TIMER_KEY, { sessionId, startedAt: startedAtMs, endedAt: null });
}

/** Freezes the timer. It stays visible until another workout starts. */
export function endWorkoutTimer(sessionId?: string): void {
  const rec = readWorkoutTimer();
  if (!rec) return;
  if (sessionId && rec.sessionId !== sessionId) return;
  if (rec.endedAt !== null) return;
  writeJson(WORKOUT_TIMER_KEY, { ...rec, endedAt: Date.now() });
}

/** Removes it entirely — used when a workout is discarded. */
export function clearWorkoutTimer(): void {
  clearJson(WORKOUT_TIMER_KEY);
}

/** Seconds on the clock: live while running, frozen once ended. */
export function elapsedSecs(rec: WorkoutTimerRecord, now: number = Date.now()): number {
  const end = rec.endedAt ?? now;
  return Math.max(0, Math.floor((end - rec.startedAt) / 1000));
}
